import {
  computed,
  Directive,
  effect,
  inject,
  OnChanges,
  signal,
  Signal,
  SimpleChanges,
  untracked,
  input,
  output,
} from '@angular/core';
import { DuelTierDataWithMeta, TiersService } from 'src/app/core/services/tiers/tiers.service';
import { PaymentService } from 'src/app/features/payments/services/payment.service';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { CurrencyPricingData } from 'src/app/core/api/player/schemas/room';
import { CurrencyAmountDisplay } from 'src/app/shared/utils/currency/currency-format';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { DuelPaymentEventData } from 'src/app/core/services/tiers/models/payment-events';

export type TierLockState = 'unlocked' | 'inprogress' | 'locked';
export type CelebrationState = 'unlock' | 'unlock-transition' | 'inprogress' | null;

@Directive()
export abstract class BaseWagerCardDirective implements OnChanges {
  // ─── Inputs ───
  readonly tier = input.required<DuelTierDataWithMeta>();
  readonly allTiers = input.required<DuelTierDataWithMeta[]>();
  readonly title = input.required<string>();
  readonly waitingOpponentsStandard = input.required<Signal<WaitingOpponentsData | undefined>>();
  readonly userBalance = input<number>();
  readonly unlockCelebrationActive = input<Signal<boolean>>();
  readonly clickEvent = output<DuelPaymentEventData>();
  readonly celebrationCompleteEvent = output<void>();

  protected readonly themeService = inject(ThemeService);
  private readonly tiersService = inject(TiersService);
  private readonly paymentService = inject(PaymentService);

  private readonly pendingInProgress = signal(false);

  constructor() {
    effect(() => {
      const active = this.unlockCelebrationActive()?.();
      if (active && this.pendingInProgress()) {
        untracked(() => {
          this.pendingInProgress.set(false);
          this.celebrationState.set('inprogress');
        });
      }
    });
  }

  readonly primaryPrizeAmount = computed(() =>
    this.formatCurrency(this.primaryAmounts(), 'reward'),
  );

  readonly secondaryPrizeAmount = computed(() =>
    this.formatCurrency(this.tier().amounts.userCurrency, 'reward'),
  );

  readonly primaryEntryAmount = computed(() =>
    this.formatCurrency(this.primaryAmounts(), 'entryFee'),
  );

  readonly secondaryEntryAmount = computed(() =>
    this.formatCurrency(this.tier().amounts.userCurrency, 'entryFee'),
  );

  private readonly primaryAmounts = computed(() =>
    this.themeService.displayCurrency() === 'match-and-user'
      ? this.tier().amounts.matchCurrency
      : this.tier().amounts.userCurrency,
  );

  readonly hasWaitingOpponent = computed(
    () => this.waitingOpponentsStandard()()?.waitingOpponents[this.tier().tierId] ?? false,
  );

  readonly isDisabled = computed(() => {
    // Every tile goes inert while any payment intent is in flight — the tapped one and its
    // siblings alike, on both tier surfaces (tier-first page and the wager drawer). A tap on a
    // sibling mid-payment is a second intent, not a retry, and the PaymentService latch would
    // drop it silently; disabling is the visible half of that guard.
    if (this.paymentService.paymentInFlight()) {
      return true;
    }
    if (!this.themeService.disableWagerCardIfNotEnoughBalance()) {
      return false;
    }
    const userBalance = this.userBalance();
    if (userBalance === undefined) {
      return false;
    }
    return userBalance < this.tier().amounts.userCurrency.entryFee;
  });

  readonly lockState = computed((): TierLockState => {
    if (this.tier().winsRequired <= this.tier().currentWins) {
      return 'unlocked';
    }
    const firstLocked = this.allTiers().find((t) => t.winsRequired > t.currentWins);
    return firstLocked?.tierId === this.tier().tierId ? 'inprogress' : 'locked';
  });

  readonly effectiveLockState = computed((): TierLockState => {
    const cs = this.celebrationState();
    if (cs === 'unlock') {
      return 'inprogress';
    }
    if (this.pendingInProgress()) {
      return 'locked';
    }
    return this.lockState();
  });

  readonly winsNeeded = computed(() => this.tier().winsRequired - this.tier().currentWins);

  readonly progressPercent = computed(() =>
    this.tier().winsRequired === 0
      ? 100
      : Math.round((this.tier().currentWins / this.tier().winsRequired) * 100),
  );

  readonly celebrationStartPercent = computed(() =>
    this.tier().winsRequired === 0
      ? 0
      : Math.round((this.tier().previousTotalWins / this.tier().winsRequired) * 100),
  );

  readonly previousCounterText = computed(() =>
    this.celebrationState() === 'unlock'
      ? `${this.tier().previousTotalWins}/${this.tier().winsRequired}`
      : `${this.tier().currentWins}/${this.tier().winsRequired}`,
  );

  readonly currentCounterText = computed(
    () => `${this.tier().currentWins}/${this.tier().winsRequired}`,
  );

  readonly celebrationState = signal<CelebrationState>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tier']?.currentValue?.celebrateUnlock === true) {
      queueMicrotask(() => this.tiersService.clearCelebrations());
      this.celebrationState.set('unlock');
    } else if (changes['tier']?.currentValue?.celebrateInProgress === true) {
      const anyUnlock = (changes['allTiers']?.currentValue ?? this.allTiers()).some(
        (t: DuelTierDataWithMeta) => t.celebrateUnlock,
      );
      if (anyUnlock) {
        this.pendingInProgress.set(true);
      } else {
        this.celebrationState.set('inprogress');
      }
    }
  }

  protected advanceCelebrationState(animationName: string): void {
    if (animationName.endsWith('unlock-transition-fade-in')) {
      this.celebrationState.set(null);
    } else if (animationName.endsWith('inprogress-fill')) {
      this.celebrationState.set(null);
    }
  }

  onClick(): void {
    if (this.isDisabled() || this.lockState() !== 'unlocked') {
      return;
    }
    this.clickEvent.emit({
      type: 'duel',
      tierData: this.tier(),
    });
  }

  private formatCurrency(
    amounts: CurrencyPricingData,
    field: 'reward' | 'entryFee',
  ): CurrencyAmountDisplay {
    return this.themeService.formatCurrencyAmount(amounts.currency, amounts[field]);
  }
}
